import type React from 'react';

import { useTranslation } from 'react-i18next';

import type { SettingKeysWithDisabledPage } from '../../domain/settings';
import { appStore } from '../../services/appStore';
import { AssetsService } from '../../services/AssetsService';
import { CloseExtensionButton } from '../CloseExtensionButton';
import { LockScreenContainer, StyledCloseButtonContainer, StyledLoginContainer } from '../guard-components/styles';

import {
  StyledDisabledImage,
  StyledDisabledSettingHeading,
  StyledDisabledSettingNew,
  StyledDisabledSettingText,
  StyledEnableButton,
} from './styles';

export enum DisabledPageViewVariant {
  Calendar = 'Calendar',
  Salesloft = 'Salesloft',
  HubSpot = 'Hubspot',
  MicrosoftDynamics = 'MicrosoftDynamics',
  Salesforce = 'Salesforce',
  Gmail = 'Gmail',
  GoogleSheets = 'GoogleSheets',
}

const uiConfig = {
  [DisabledPageViewVariant.Calendar]: {
    icon: 'empty-state-girl-calendar.svg',
    heading: 'inactive.calendar.heading',
    text: 'inactive.calendar.text',
    newBadge: false,
  },
  [DisabledPageViewVariant.Salesloft]: {
    icon: 'empty-state-salesloft.svg',
    heading: 'inactive.salesloft.heading',
    text: 'inactive.crm.text',
    newBadge: false,
  },
  [DisabledPageViewVariant.HubSpot]: {
    icon: 'empty-state-girl-hubspot.svg',
    heading: 'inactive.hubspot.heading',
    text: 'inactive.crm.text',
    newBadge: false,
  },
  [DisabledPageViewVariant.MicrosoftDynamics]: {
    icon: 'empty-state-girl-microsoft-dynamics.svg',
    heading: 'inactive.microsoft.dynamics.heading',
    text: 'inactive.crm.text',
    newBadge: true,
  },
  [DisabledPageViewVariant.Salesforce]: {
    icon: 'empty-state-girl-salesforce.svg',
    heading: 'inactive.salesforce.heading',
    text: 'inactive.crm.text',
    newBadge: true,
  },
  [DisabledPageViewVariant.Gmail]: {
    icon: 'empty-state-girl-gmail.svg',
    heading: 'inactive.gmail.heading',
    text: 'inactive.gmail.text',
    newBadge: true,
  },
  [DisabledPageViewVariant.GoogleSheets]: {
    icon: 'empty-state-girl-google-sheets.svg',
    heading: 'inactive.google_sheets.heading',
    text: 'inactive.google_sheets.text',
    newBadge: true,
  },
};

interface IDisabledPageViewProps {
  settingConfig: { key: SettingKeysWithDisabledPage; pageVariant: DisabledPageViewVariant };
}

export const DisabledPageView: React.FC<IDisabledPageViewProps> = ({ settingConfig }) => {
  const { key, pageVariant } = settingConfig;
  const { t } = useTranslation();

  const handleEnableSetting = () => {
    appStore.setSettingsStorageValue({ shouldEnableToggle: key });
    appStore.setSettingsPage();
  };

  const { icon, text, heading, newBadge } = uiConfig[pageVariant];

  return (
    <LockScreenContainer variant="login">
      <StyledCloseButtonContainer data-automation="DisabledCalendarClose">
        <CloseExtensionButton />
      </StyledCloseButtonContainer>
      <StyledDisabledImage src={AssetsService.assetUrl(`/images/${icon}`)} />
      <StyledLoginContainer isDisabledPageView>
        {newBadge ? (
          <StyledDisabledSettingNew data-automation="DisabledCalendarSettingNew">{`${t(
            'common.new'
          )} \u{1F389}`}</StyledDisabledSettingNew>
        ) : null}
        <StyledDisabledSettingHeading data-automation="DisabledCalendarSettingHeading">
          {t(heading)}
        </StyledDisabledSettingHeading>
        <StyledDisabledSettingText data-automation="DisabledCalendarSettingText">{t(text)}</StyledDisabledSettingText>
        <StyledEnableButton onClick={handleEnableSetting} data-automation="DisabledCalendarEnableButton">
          {t('inactive.calendar.button')}
        </StyledEnableButton>
      </StyledLoginContainer>
    </LockScreenContainer>
  );
};
